// Recovery is scoped to the authenticated SDK subject, never a profile/URL identity.
const PREFIX = "privana:academy-learning:v2:";
const OWNER = "privana:academy-learning:owner";
const LEGACY = [
  "academy-command:",
  "learning-key:",
  "learning-result:",
  "request-command:",
  "academy:request:",
];
let activeScope = null;
const listeners = new Set();
const pendingCommands = new Set();
export const subscribeLearningScope = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const currentLearningScope = () => activeScope;
const notifyScope = () => {
  for (const listener of listeners) listener();
};
const storage = () => globalThis.window?.sessionStorage;
export function suspendLearningScope() {
  for (const controller of pendingCommands) controller.abort();
  pendingCommands.clear();
  const changed = activeScope !== null;
  activeScope = null;
  if (changed) notifyScope();
}
export function clearLearningRecovery() {
  for (const controller of pendingCommands) controller.abort();
  pendingCommands.clear();
  for (const s of [storage(), globalThis.window?.localStorage])
    if (s)
      for (const key of Object.keys(s))
        if (
          key.startsWith(PREFIX) ||
          key === OWNER ||
          LEGACY.some((p) => key.startsWith(p))
        )
          s.removeItem(key);
  activeScope = null;
  notifyScope();
}
export function synchronizeLearningAccount(account) {
  const s = storage();
  let owner;
  try {
    owner = JSON.parse(s?.getItem(OWNER) || "null");
  } catch {}
  if (!account || (owner && owner.account !== account)) clearLearningRecovery();
  for (const s of [storage(), globalThis.window?.localStorage])
    if (s)
      for (const key of Object.keys(s))
        if (LEGACY.some((p) => key.startsWith(p))) s.removeItem(key);
}
export function activateLearningScope(account, club) {
  synchronizeLearningAccount(account);
  if (!account || !club) {
    clearLearningRecovery();
    return null;
  }
  const scope = JSON.stringify({ account, club });
  if (storage()?.getItem(OWNER) !== scope) clearLearningRecovery();
  const changed = activeScope !== scope;
  activeScope = scope;
  storage()?.setItem(OWNER, scope);
  if (changed) notifyScope();
  return scope;
}
function assertCurrent(scope) {
  if (!scope || activeScope !== scope || storage()?.getItem(OWNER) !== scope)
    throw Object.assign(new Error("Learning account changed"), {
      code: "forbidden",
    });
}
export const recoveryKey = (scope, channel, action, payload) =>
  `${PREFIX}${scope}:${JSON.stringify({ channel, action, payload })}`;
export function recoveryRecord(scope, channel, action, payload) {
  assertCurrent(scope);
  const key = recoveryKey(scope, channel, action, payload);
  let record;
  try {
    record = JSON.parse(storage()?.getItem(key) || "null");
  } catch {
    storage()?.removeItem(key);
  }
  return record && typeof record.key === "string" ? record : null;
}
export function discardRecovery(scope, channel, action, payload) {
  assertCurrent(scope);
  storage()?.removeItem(recoveryKey(scope, channel, action, payload));
}
// Retain idempotency identities while removing acknowledged payloads after access is revoked.
export function redactRecoveryResults(scope) {
  assertCurrent(scope);
  const s = storage();
  if (!s) return;
  for (const key of Object.keys(s)) {
    if (!key.startsWith(`${PREFIX}${scope}:`)) continue;
    try {
      const record = JSON.parse(s.getItem(key));
      if (record?.key && record.result) s.setItem(key, JSON.stringify({key: record.key}));
    } catch { s.removeItem(key); }
  }
}
export async function replayRecoveredCommand(
  scope,
  channel,
  action,
  payload,
  send,
) {
  assertCurrent(scope);
  const record = recoveryRecord(scope, channel, action, payload) || {
    key: crypto.randomUUID(),
  };
  const location = recoveryKey(scope, channel, action, payload);
  storage()?.setItem(location, JSON.stringify(record));
  // Even acknowledged results must go back through current server authorisation.
  const controller = new AbortController();
  pendingCommands.add(controller);
  try {
    const result = await send(action, payload, record.key, controller.signal);
    assertCurrent(scope); // A late previous-account response cannot restore recovery data.
    storage()?.setItem(location, JSON.stringify({ key: record.key, result }));
    return result;
  } catch (error) {
    assertCurrent(scope);
    throw error;
  } finally {
    pendingCommands.delete(controller);
  }
}
